import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    forceCenter,
    forceCollide,
    forceLink,
    forceManyBody,
    forceSimulation,
    forceX,
    forceY,
    type Simulation,
    type SimulationLinkDatum,
    type SimulationNodeDatum,
} from 'd3-force';
import { select } from 'd3-selection';
// Register d3-transition side-effects so ``select().transition()`` is
// available for the reset-view animation.
import 'd3-transition';
import { zoom as d3Zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import type { KuralGraphEntry, PaalSlug } from '../data/types';
import { useGraphIndex } from '../hooks/useGraphIndex';
import './CorpusGraph.css';

// ------------------------------ types ------------------------------ //

interface SimNode extends SimulationNodeDatum {
    n: number;              // kural number
    paal: PaalSlug;
    first_line_ta: string;
    verse_ta: string[];
    gist_en: string;
}

interface SimEdge extends SimulationLinkDatum<SimNode> {
    weight: number;         // shared-tag count, used for edge opacity
}

// ------------------------------ geometry --------------------------- //

const VIEW_W = 1200;
const VIEW_H = 780;
const NODE_R = 3.2;

// Cool the simulation aggressively so the initial reveal completes
// quickly, then keep it gently alive.
const INITIAL_ALPHA = 1;
const ALPHA_TARGET = 0.02;
const ALPHA_DECAY = 0.055;
const VELOCITY_DECAY = 0.55;

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 10;

// ------------------------------ helpers ---------------------------- //

interface HoverInfo {
    node: SimNode;
    clientX: number;
    clientY: number;
}

// Build the deduplicated, undirected edge list from every kural's
// ``related[]`` field. Same-chapter siblings are already excluded by
// the pipeline (link_kurals.py), so what remains is purely thematic.
function buildEdges(
    kurals: KuralGraphEntry[],
    nodeByN: Map<number, SimNode>
): SimEdge[] {
    const seen = new Set<string>();
    const edges: SimEdge[] = [];
    for (const k of kurals) {
        for (const rel of k.related) {
            const [a, b] = k.n < rel.n ? [k.n, rel.n] : [rel.n, k.n];
            const key = `${a}-${b}`;
            if (seen.has(key)) continue;
            seen.add(key);
            const source = nodeByN.get(a);
            const target = nodeByN.get(b);
            if (!source || !target) continue;
            edges.push({ source, target, weight: rel.shared_tags.length });
        }
    }
    return edges;
}

// ------------------------------ component -------------------------- //

/**
 * The corpus graph: 1,330 kurals as small paal-coloured nodes, with a
 * thematic edge between any two kurals that share at least one theme
 * (using the same curated top-5 ``related[]`` links the focus view
 * uses). Same-chapter connections are deliberately excluded — this
 * view is the thematic-only view.
 *
 * Nodes are React-rendered ONCE at mount; every subsequent simulation
 * tick imperatively mutates ``cx``/``cy``/``x1``/``y1``/``x2``/``y2``
 * via refs so we don't trigger 1,330 React re-renders per frame.
 */
export default function CorpusGraph() {
    const graph = useGraphIndex();
    const navigate = useNavigate();
    const { search } = useLocation();

    const svgRef = useRef<SVGSVGElement>(null);
    const contentRef = useRef<SVGGElement>(null);
    const nodeCirclesRef = useRef<(SVGCircleElement | null)[]>([]);
    const edgeLinesRef = useRef<(SVGLineElement | null)[]>([]);
    const simRef = useRef<Simulation<SimNode, SimEdge> | null>(null);
    const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

    const [hover, setHover] = useState<HoverInfo | null>(null);
    const [ready, setReady] = useState(false);

    // Build stable node + edge lists from the loaded graph index.
    const { nodes, edges } = useMemo(() => {
        if (!graph.data) return { nodes: [], edges: [] };
        const ns: SimNode[] = graph.data.kurals.map((k) => ({
            n: k.n,
            paal: k.paal,
            first_line_ta: k.first_line_ta,
            verse_ta: k.verse_ta,
            gist_en: k.gist_en,
        }));
        const byN = new Map(ns.map((n) => [n.n, n]));
        const es = buildEdges(graph.data.kurals, byN);
        return { nodes: ns, edges: es };
    }, [graph.data]);

    // Attach the force simulation once nodes are known.
    useLayoutEffect(() => {
        if (nodes.length === 0) return;

        // Seed positions in a small random cloud so forces have something
        // to push against.
        for (const n of nodes) {
            if (n.x == null) n.x = (Math.random() - 0.5) * 200;
            if (n.y == null) n.y = (Math.random() - 0.5) * 200;
        }

        const sim = forceSimulation<SimNode, SimEdge>(nodes)
            .force(
                'link',
                forceLink<SimNode, SimEdge>(edges)
                    .id((n) => n.n)
                    .distance(30)
                    .strength(0.55)
            )
            .force('charge', forceManyBody<SimNode>().strength(-22))
            .force('centre', forceCenter(0, 0).strength(0.04))
            // Per-node gravity toward the origin — keeps every node near the
            // viewBox centre instead of allowing the graph's centre of mass
            // to drift over time. Weaker than the link force so clusters
            // still form organically.
            .force('x', forceX<SimNode>(0).strength(0.012))
            .force('y', forceY<SimNode>(0).strength(0.012))
            .force('collide', forceCollide<SimNode>().radius(NODE_R + 1.5))
            .alpha(INITIAL_ALPHA)
            .alphaTarget(ALPHA_TARGET)
            .alphaDecay(ALPHA_DECAY)
            .velocityDecay(VELOCITY_DECAY);

        // Imperative per-tick attribute updates — bypass React so 1,330
        // nodes stay smooth.
        const paint = () => {
            const nodeEls = nodeCirclesRef.current;
            for (let i = 0; i < nodes.length; i++) {
                const el = nodeEls[i];
                if (!el) continue;
                el.setAttribute('cx', String(nodes[i].x ?? 0));
                el.setAttribute('cy', String(nodes[i].y ?? 0));
            }
            const edgeEls = edgeLinesRef.current;
            for (let i = 0; i < edges.length; i++) {
                const el = edgeEls[i];
                if (!el) continue;
                const s = edges[i].source as SimNode;
                const t = edges[i].target as SimNode;
                el.setAttribute('x1', String(s.x ?? 0));
                el.setAttribute('y1', String(s.y ?? 0));
                el.setAttribute('x2', String(t.x ?? 0));
                el.setAttribute('y2', String(t.y ?? 0));
            }
        };

        sim.on('tick', paint);
        paint();

        // After ~600 ms of settling, mark ready — the reveal animation
        // (nodes fade in) can now play in CSS.
        const readyTimer = window.setTimeout(() => setReady(true), 400);

        simRef.current = sim;
        return () => {
            window.clearTimeout(readyTimer);
            sim.stop();
        };
    }, [nodes, edges]);

    // Pause simulation when the tab is hidden — no reason to spend CPU on
    // an off-screen graph.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const onVis = () => {
            const sim = simRef.current;
            if (!sim) return;
            if (document.hidden) sim.stop();
            else sim.alpha(0.15).restart();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    // Attach d3-zoom for wheel-zoom + drag-to-pan. We wire it AFTER the
    // simulation attaches so the content group exists.
    useEffect(() => {
        const svg = svgRef.current;
        const content = contentRef.current;
        if (!svg || !content) return;

        const zoomBehavior = d3Zoom<SVGSVGElement, unknown>()
            .scaleExtent([ZOOM_MIN, ZOOM_MAX])
            .filter((event: Event) => {
                // Allow pan on empty-space drag; block right-click.
                const target = event.target as SVGElement | null;
                if (event.type === 'mousedown' && target?.tagName === 'circle') {
                    // Let the circle receive click; skip pan when dragging on a node.
                    return false;
                }
                return !('button' in event) || (event as MouseEvent).button === 0;
            })
            .on('zoom', (event) => {
                content.setAttribute('transform', event.transform.toString());
            });

        zoomRef.current = zoomBehavior;
        select(svg).call(zoomBehavior);

        // Initial fit-to-view: nudge the content into the SVG viewBox
        // center at scale 1.
        select(svg).call(zoomBehavior.transform, zoomIdentity);

        return () => {
            select(svg).on('.zoom', null);
        };
    }, [nodes.length]);

    // ------------- hover ------------- //

    const onNodeEnter = useCallback(
        (n: SimNode) => (e: React.MouseEvent<SVGCircleElement>) => {
            setHover({ node: n, clientX: e.clientX, clientY: e.clientY });
        },
        []
    );
    const onNodeLeave = useCallback(() => setHover(null), []);
    const onNodeMove = useCallback(
        (e: React.MouseEvent<SVGCircleElement>) => {
            setHover((h) => (h ? { ...h, clientX: e.clientX, clientY: e.clientY } : h));
        },
        []
    );

    const onNodeClick = useCallback(
        (n: SimNode) => () => {
            navigate(`/kural/${n.n}${search}`);
        },
        [navigate, search]
    );

    // ------------- reset view ------------- //

    const resetView = useCallback(() => {
        const svg = svgRef.current;
        const zoomBehavior = zoomRef.current;
        if (!svg || !zoomBehavior) return;
        select(svg)
            .transition()
            .duration(400)
            .call(zoomBehavior.transform, zoomIdentity);
    }, []);

    // ------------- render ------------- //

    if (graph.error) {
        return (
            <div className="corpus-graph corpus-graph--error" role="alert">
                Could not load the corpus.
            </div>
        );
    }
    if (graph.loading || !graph.data) {
        return (
            <div className="corpus-graph corpus-graph--loading" aria-live="polite">
                <span className="corpus-graph__pulse" aria-hidden="true" />
                <span>Loading the corpus…</span>
            </div>
        );
    }

    return (
        <div className="corpus-graph">
            <svg
                ref={svgRef}
                className={`corpus-graph__svg${ready ? ' is-ready' : ''}`}
                viewBox={`${-VIEW_W / 2} ${-VIEW_H / 2} ${VIEW_W} ${VIEW_H}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label="Thematic connections across the 1,330 kurals — drag to pan, scroll to zoom"
            >
                <g ref={contentRef}>
                    {/* --- edges (rendered first, behind nodes) --- */}
                    <g className="corpus-graph__edges">
                        {edges.map((e, i) => (
                            <line
                                key={i}
                                ref={(el) => {
                                    edgeLinesRef.current[i] = el;
                                }}
                                className="corpus-graph__edge"
                                strokeWidth={e.weight >= 3 ? 0.75 : 0.5}
                                opacity={0.35 + Math.min(0.45, e.weight * 0.12)}
                            />
                        ))}
                    </g>

                    {/* --- nodes --- */}
                    <g className="corpus-graph__nodes">
                        {nodes.map((n, i) => (
                            <circle
                                key={n.n}
                                ref={(el) => {
                                    nodeCirclesRef.current[i] = el;
                                }}
                                r={NODE_R}
                                className={`corpus-graph__node corpus-graph__node--${n.paal}`}
                                onMouseEnter={onNodeEnter(n)}
                                onMouseLeave={onNodeLeave}
                                onMouseMove={onNodeMove}
                                onClick={onNodeClick(n)}
                                onFocus={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setHover({
                                        node: n,
                                        clientX: rect.left + rect.width / 2,
                                        clientY: rect.top,
                                    });
                                }}
                                onBlur={onNodeLeave}
                                tabIndex={-1}
                                aria-label={`Kural ${n.n}: ${n.first_line_ta}`}
                            />
                        ))}
                    </g>
                </g>
            </svg>

            {/* Overlay controls: legend + reset */}
            <div className="corpus-graph__legend" aria-label="Paal legend">
                <div className="corpus-graph__legend-title">
                    {edges.length.toLocaleString()} thematic links across{' '}
                    {nodes.length.toLocaleString()} kurals
                </div>
                <ul className="corpus-graph__legend-list">
                    <li>
                        <span
                            className="corpus-graph__swatch corpus-graph__swatch--aram"
                            aria-hidden="true"
                        />
                        Aram <span className="corpus-graph__legend-ta" lang="ta">அறம்</span>
                        <span className="corpus-graph__legend-note">virtue</span>
                    </li>
                    <li>
                        <span
                            className="corpus-graph__swatch corpus-graph__swatch--porul"
                            aria-hidden="true"
                        />
                        Porul <span className="corpus-graph__legend-ta" lang="ta">பொருள்</span>
                        <span className="corpus-graph__legend-note">wealth</span>
                    </li>
                    <li>
                        <span
                            className="corpus-graph__swatch corpus-graph__swatch--inbam"
                            aria-hidden="true"
                        />
                        Inbam <span className="corpus-graph__legend-ta" lang="ta">இன்பம்</span>
                        <span className="corpus-graph__legend-note">love</span>
                    </li>
                </ul>
            </div>

            <div className="corpus-graph__controls">
                <button
                    type="button"
                    className="corpus-graph__reset"
                    onClick={resetView}
                    aria-label="Reset zoom and pan"
                    title="Reset view"
                >
                    Reset view
                </button>
                <div className="corpus-graph__hint" aria-hidden="true">
                    scroll to zoom · drag to pan · click a node
                </div>
            </div>

            {hover && (
                <div
                    className="corpus-graph__tooltip"
                    role="tooltip"
                    style={{
                        left: hover.clientX,
                        top: hover.clientY,
                    }}
                >
                    <div className="corpus-graph__tooltip-n">Kural {hover.node.n}</div>
                    <div lang="ta" className="corpus-graph__tooltip-ta">
                        {(hover.node.verse_ta && hover.node.verse_ta.length > 0
                            ? hover.node.verse_ta
                            : [hover.node.first_line_ta]
                        ).map((line, i) => (
                            <span key={i} className="corpus-graph__tooltip-verse-line">
                                {line}
                            </span>
                        ))}
                    </div>
                    <div className="corpus-graph__tooltip-sub">{hover.node.gist_en}</div>
                </div>
            )}
        </div>
    );
}
